import { palette } from '../../lib/palette';
import { ICONS, type IconComponent } from '../ui/icons';

/**
 * Fonte única dos blocos do Flow Builder: nome, descrição, ícone (nome oficial
 * do Cloudscape) e grupo. A paleta, os nós do canvas e o minimapa leem daqui —
 * antes cada um tinha a sua cópia, com cores e nomes divergentes.
 *
 * Cor indica o grupo (não o bloco), usando tokens da paleta do produto
 * (`src/lib/palette.ts`, extraída do Cloudscape).
 */

export type BlockGroup = 'inicio' | 'mensagens' | 'logica' | 'integracoes';

export interface BlockGroupStyle {
  label: string;
  /** Ícone e marcador lateral do nó */
  accent: string;
  /** Fundo do ícone */
  tint: string;
}

export const BLOCK_GROUPS: Record<BlockGroup, BlockGroupStyle> = {
  inicio:      { label: 'Início',                     accent: palette.green.dark1,  tint: palette.green.light3 },
  mensagens:   { label: 'Mensagens',                  accent: palette.blue.base,    tint: palette.blue.light3 },
  logica:      { label: 'Lógica',                     accent: palette.yellow.dark2, tint: palette.yellow.light3 },
  integracoes: { label: 'Inteligência e integrações', accent: palette.purple.base,  tint: palette.purple.light3 },
};

export interface FlowBlock {
  label: string;
  description: string;
  /** Nome oficial do ícone no Cloudscape (conjunto fechado de 144 nomes). */
  iconName: IconComponent;
  group: BlockGroup;
}

export const FLOW_BLOCKS: Record<string, FlowBlock> = {
  trigger:              { label: 'Início',           description: 'Dispara o fluxo',         iconName: ICONS.LightningBolt, group: 'inicio' },
  text:                 { label: 'Texto',            description: 'Envia uma mensagem',      iconName: ICONS.SMS,           group: 'mensagens' },
  image:                { label: 'Imagem',           description: 'Envia uma foto',          iconName: ICONS.Camera,        group: 'mensagens' },
  video:                { label: 'Vídeo',            description: 'Envia um vídeo',          iconName: ICONS.Play,          group: 'mensagens' },
  audio:                { label: 'Áudio',            description: 'Envia um áudio',          iconName: ICONS.AudioFull,         group: 'mensagens' },
  document:             { label: 'Arquivo',          description: 'Envia um documento',      iconName: ICONS.File,          group: 'mensagens' },
  button:               { label: 'Botões',           description: 'Opções para responder',   iconName: ICONS.Code,           group: 'mensagens' },
  condition:            { label: 'Condição',         description: 'Ramifica pela resposta',  iconName: ICONS.Diagram2,      group: 'logica' },
  delay:                { label: 'Espera',           description: 'Aguarda um tempo',        iconName: ICONS.Clock,         group: 'logica' },
  stop:                 { label: 'Encerrar',         description: 'Finaliza o fluxo',        iconName: ICONS.Stop,        group: 'logica' },
  ai:                   { label: 'Atendente IA',     description: 'Conversa com IA',          iconName: ICONS.Sparkle,       group: 'integracoes' },
  httprest:             { label: 'Requisição HTTP',  description: 'Chama uma API externa',   iconName: ICONS.GlobeAmericas,              group: 'integracoes' },
  integration_perfex:   { label: 'Perfex CRM',       description: 'Atualiza o CRM',          iconName: ICONS.Building,      group: 'integracoes' },
  integration_chatwoot: { label: 'Chatwoot',         description: 'Envia ao atendimento',    iconName: ICONS.QuestionMarkWithCircle, group: 'integracoes' },
  sequence_enroll:      { label: 'Sequência',        description: 'Inscreve numa sequência', iconName: ICONS.Calendar,           group: 'integracoes' },
  meta_conversion:      { label: 'Conversão Meta Ads', description: 'Avisa o anúncio que converteu', iconName: ICONS.Charts, group: 'integracoes' },
  action:               { label: 'Ação',             description: 'Bloco legado',            iconName: ICONS.Settings,       group: 'logica' },
};

/** Blocos exibidos na paleta, na ordem, por grupo (integrações dependem de configuração). */
export const PALETTE_ORDER: Record<BlockGroup, string[]> = {
  inicio: ['trigger'],
  mensagens: ['text', 'image', 'video', 'audio', 'document', 'button'],
  logica: ['condition', 'delay', 'stop'],
  integracoes: ['ai', 'httprest', 'sequence_enroll', 'meta_conversion', 'integration_perfex', 'integration_chatwoot'],
};

const FALLBACK: FlowBlock = { label: 'Bloco', description: '', iconName: ICONS.Apps, group: 'logica' };

export function getBlock(type: string | undefined): FlowBlock {
  return (type && FLOW_BLOCKS[type]) || FALLBACK;
}

export function getBlockStyle(type: string | undefined): BlockGroupStyle {
  return BLOCK_GROUPS[getBlock(type).group];
}
